import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AssignmentSubmission.css';

const AssignmentSubmission = ({ courseId, weekNo, studentId }) => {
  const [assignment, setAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    loadAssignment();
  }, [weekNo]);

  const loadAssignment = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/assignments/by-course/${courseId}`);
      const weekAssignment = response.data.find(a => a.week_no === weekNo);
      setAssignment(weekAssignment);

      if (weekAssignment) {
        // Load existing submission if exists
        const submissionsRes = await axios.get(`/api/assignments/submissions/${studentId}/${courseId}`);
        const existing = submissionsRes.data.find(s => s.assignment_id === weekAssignment.assignment_id);
        setSubmission(existing);
      }
    } catch (error) {
      console.error('Failed to load assignment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(selectedFile.type)) {
        setToast({ type: 'error', message: 'Only PDF and DOCX files are allowed' });
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        setToast({ type: 'error', message: 'File size must be less than 10MB' });
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setToast({ type: 'error', message: 'Please select a file to submit' });
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('assignment_id', assignment.assignment_id);
    formData.append('student_id', studentId);

    try {
      await axios.post('/api/assignments/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setToast({ type: 'success', message: 'Assignment submitted successfully!' });
      setFile(null);
      loadAssignment();
    } catch (error) {
      setToast({ type: 'error', message: error.response?.data?.message || 'Failed to submit assignment' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="assignment-loader">Loading assignment...</div>;

  if (!assignment) {
    return <div className="no-assignment">No assignment for this week</div>;
  }

  return (
    <div className="assignment-submission-container">
      <div className="assignment-card">
        <div className="assignment-header">
          <h3>📝 {assignment.title}</h3>
          {assignment.due_date && (
            <span className="due-date">Due: {new Date(assignment.due_date).toLocaleDateString()}</span>
          )}
        </div>

        <div className="assignment-description">
          {assignment.description}
        </div>

        {submission && submission.status !== 'rejected' ? (
          <div className="submission-status">
            <div className="status-card">
              <div className="status-header">
                <span className="status-badge status-submitted">✓ Submitted</span>
              </div>

              <div className="submission-details">
                <div className="detail-item">
                  <span className="label">Submitted Date:</span>
                  <span className="value">{new Date(submission.submitted_at).toLocaleString()}</span>
                </div>

                {submission.status === 'evaluated' && (
                  <>
                    <div className="detail-item">
                      <span className="label">Marks:</span>
                      <span className="value">{submission.marks}</span>
                    </div>

                    {submission.feedback && (
                      <div className="feedback-section">
                        <h4>Feedback from Faculty</h4>
                        <p>{submission.feedback}</p>
                      </div>
                    )}
                  </>
                )}

                {submission.status === 'submitted' && (
                  <div className="evaluation-pending">
                    <span className="badge-info">⏳ Awaiting Evaluation</span>
                  </div>
                )}
              </div>

              <div className="submission-actions">
                <a
                  href={`/files/download/${submission.file_path}`}
                  className="btn-secondary"
                >
                  📥 Download Submission
                </a>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="submission-form">
            {submission?.status === 'rejected' && (
              <div className="feedback-section" style={{ marginBottom: 12, borderColor: '#dc2626' }}>
                <h4 style={{ color: '#b91c1c' }}>Submission Rejected</h4>
                <p>{submission.feedback || 'Your faculty has rejected this submission. Please correct and resubmit.'}</p>
              </div>
            )}
            <div className="form-group">
              <label>Upload Your Assignment *</label>
              <div className="file-upload">
                <input
                  type="file"
                  id="assignment-file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  disabled={submitting}
                />
                <label htmlFor="assignment-file" className="file-label">
                  {file ? (
                    <>
                      <span className="file-icon">✓</span>
                      <span className="file-name">{file.name}</span>
                    </>
                  ) : (
                    <>
                      <span className="upload-icon">📎</span>
                      <span>Click to upload or drag and drop</span>
                      <span className="file-hint">PDF or DOCX (max 10MB)</span>
                    </>
                  )}
                </label>
              </div>
            </div>

            <button type="submit" className="btn-primary" disabled={submitting || !file}>
              {submitting ? 'Submitting...' : (submission?.status === 'rejected' ? 'Resubmit Assignment' : 'Submit Assignment')}
            </button>
          </form>
        )}
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <span>{toast.message}</span>
          <button className="toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}
    </div>
  );
};

export default AssignmentSubmission;
